import angular from 'angular'

angular.module('vend.ui').factory('ModalService', [
  '$document',
  '$compile',
  '$controller',
  '$rootScope',
  '$q',
  '$templateRequest',
  '$timeout',
  'vdOverlayService',
  function (
    $document,
    $compile,
    $controller,
    $rootScope,
    $q,
    $templateRequest,
    $timeout,
    vdOverlayService
  ) {
    function ModalService() {
      const self = this

      //  Track open modals.
      self.openModals = []

      //  Returns a promise which gets the template, either
      //  from the template parameter or via a request to the
      //  template url parameter.
      const getTemplate = function (template, templateUrl) {
        const deferred = $q.defer()
        if (template) {
          deferred.resolve(template)
        } else if (templateUrl) {
          $templateRequest(templateUrl, true).then(
            function (template) {
              deferred.resolve(template)
            },
            function (error) {
              deferred.reject(error)
            }
          )
        } else {
          deferred.reject('No template or templateUrl has been specified.')
        }
        return deferred.promise
      }

      //  Adds an element to the DOM as the last child of its container
      const appendChild = function (parent, child) {
        const children = parent.children()
        if (children.length > 0) {
          angular.element(children[children.length - 1]).after(child)
          return
        }
        parent.prepend(child)
      }

      //  Close all modals, providing the given result to the close promise.
      self.closeModals = function (result) {
        while (self.openModals.length) {
          self.openModals[0].close(result)
        }
      }

      self.showModal = function (options) {
        //  Get the body of the document, we'll add the modal to this.
        const body = angular.element($document[0].body)

        //  Create a deferred we'll resolve when the modal is ready.
        let deferred = $q.defer()

        //  Validate the input parameters.
        const controllerName = options.controller
        if (!controllerName) {
          deferred.reject('No controller has been specified.')
          return deferred.promise
        }

        //  Get the actual html of the template.
        getTemplate(options.template, options.templateUrl)
          .then(function (template) {
            //  The main modal object we will build.
            let modal = {}

            //  Create a new scope for the modal.
            let modalScope = (options.scope || $rootScope).$new()

            // Automatically close the modal if the route changes. We bind this in a $timeout as sometimes (particularly
            // if using ui-router) locationChangeSuccess hasn't fired yet when the new route's controller code is run
            // and if that code triggered a modal it would get closed immediately when locationChangeSuccess was fired.
            let rootScopeOnClose
            $timeout(function () {
              rootScopeOnClose = $rootScope.$on(
                '$locationChangeSuccess',
                function () {
                  cleanUpClose()
                }
              )
            })

            //  Create the inputs object to the controller - this will include
            //  the scope, as well as all inputs provided.
            //  We will also create a deferred that is resolved with a provided
            //  close function. The controller can then call 'close(result)'.
            let closeDeferred = $q.defer()

            let inputs = {
              $scope: modalScope,
              close: closeModal,
            }

            //  If we have provided any inputs, pass them to the controller.
            if (options.inputs) angular.extend(inputs, options.inputs)

            //  Create the controller, explicitly specifying the scope to use.
            const controllerObjBefore = modalScope[options.controllerAs]
            const modalController = $controller(
              options.controller,
              inputs,
              false,
              options.controllerAs
            )

            if (options.controllerAs && controllerObjBefore) {
              angular.extend(modalController, controllerObjBefore)
            }

            //  Compile then link the template element, building the actual element.
            const linkFn = $compile(template)
            let modalElement = linkFn(modalScope)

            // Show overlay if requested
            if (options.showOverlay) {
              vdOverlayService.showOverlay()
            }

            //  Then, append the modal to the dom.
            if (options.appendElement) {
              // append to custom append element
              appendChild(options.appendElement, modalElement)
            } else {
              // append to body when no custom append element is specified
              appendChild(body, modalElement)
            }

            //  Populate the modal object...
            modal.controller = modalController
            modal.scope = modalScope
            modal.element = modalElement
            modal.close = closeDeferred.promise
            modal.closed = closeDeferred.promise
            modal.closeModal = closeModal

            //  ...which is passed to the caller via the promise.
            deferred.resolve(modal)

            function closeModal(result) {
              cleanUpClose(result)
            }

            //  We can track this modal in our open modals.
            self.openModals.push({ modal, close: inputs.close })

            function cleanUpClose(result) {
              if (!modalElement) {
                // Clean up has already run
                return
              }

              closeDeferred.resolve(result)

              // remove event watcher
              rootScopeOnClose && rootScopeOnClose()

              //  Let angular remove the element.
              modalElement.remove()

              // Remove overlay if requested
              if (options.showOverlay) {
                vdOverlayService.hideOverlay()
              }

              //  We can now clean up the scope
              modalScope.$destroy()

              //  Remove the modal from the set of open modals.
              for (let i = 0; i < self.openModals.length; i++) {
                if (self.openModals[i].modal === modal) {
                  self.openModals.splice(i, 1)
                  break
                }
              }

              //  Unless we null out all of these objects we seem to suffer
              //  from memory leaks, if anyone can explain why then I'd
              //  be very interested to know.
              inputs.close = null
              deferred = null
              closeDeferred = null
              modal = null
              inputs = null
              modalElement = null
              modalScope = null
            }
          })
          .catch(function (error) {
            deferred.reject(error)
          })

        return deferred.promise
      }
    }

    return new ModalService()
  },
])
